/**
 * Copy for the `/easy-quote` screen (`EasyQuote.Components.tsx`, `EasyQuoteGuestForm.tsx`), in
 * `en`, `fr` and `es`.
 */
import { getBrandInfo } from "@ryerson/frontend.common";

/**
 * One language's Easy Quote copy. `dontHaveLoginText`, `guestCheckoutDescription` and the six
 * "Process steps" texts are not read by any component.
 */
export interface EasyQuoteLanguageText {
	// Heading and description
	dontHaveLoginText: string;
	guestCheckoutDescription: string;

	// Form fields
	customerNumberLabel: string;
	soldToZipCodeLabel: string;
	checkoutAsGuestButton: string;

	// Process steps
	validateAccessText: string;
	viewQuoteDetailsText: string;
	enterPurchaseOrderText: string;
	enterInformationText: string;
	selectPaymentMethodText: string;
	submitOrderText: string;

	// Error messages
	genericErrorMessage: string;
	genericErrorMessage2: string;
	validationErrorMessage: string;
	// The build's brand address (`getBrandInfo().email`), shown between the two generic messages.
	brandedEmail: string;
	easyQuotePageTitle: string;
	checkout: string;
	login: string;
	or: string;
	back: string;
	continue: string;
}

interface EasyQuoteText {
	[key: string]: EasyQuoteLanguageText;
}

/** Easy Quote copy keyed by language code; callers fall back to `en`. */
export const EasyQuoteContent: EasyQuoteText = {
	en: {
		dontHaveLoginText: "Don't have a log in?",
		guestCheckoutDescription:
			"You can request access or simply place your order by converting your quote as a guest",
		customerNumberLabel: "Customer Number",
		soldToZipCodeLabel: "Sold To Zip Code",
		checkoutAsGuestButton: "Check out as Guest",
		validateAccessText: "Validate your access",
		viewQuoteDetailsText: "View quote details",
		enterPurchaseOrderText: "Enter your purchase order number",
		enterInformationText: "Enter your information",
		selectPaymentMethodText: "Select payment method",
		submitOrderText: "Submit Order",
		genericErrorMessage: "We couldn't find any matching results. Please contact ",

		genericErrorMessage2: " or chat with us so we can help you find what you need.",
		validationErrorMessage:
			"Please review the highlighted fields and make any necessary changes.",
		brandedEmail: getBrandInfo().email,
		easyQuotePageTitle: "Ready to Order Your Quote?",
		checkout: "Check Out as Guest",
		login: "Log In",
		or: "or",
		back: "Back",
		continue: "Continue",
	},
	fr: {
		dontHaveLoginText: "Vous n'avez pas d'identifiants de connexion?",
		guestCheckoutDescription:
			"Vous pouvez demander l'accès ou simplement effectuer votre commande en convertissant votre devis en devis d'invité",
		customerNumberLabel: "Numéro de client",
		soldToZipCodeLabel: "Code postal de vente",
		checkoutAsGuestButton: "Passer à la caisse en tant qu'invité",
		validateAccessText: "Valider votre accès",
		viewQuoteDetailsText: "Voir les détails du devis",
		enterPurchaseOrderText: "Entrer votre numéro de bon de commande",
		enterInformationText: "Entrer vos informations",
		selectPaymentMethodText: "Sélectionner un mode de paiement",
		submitOrderText: "Soumettre la commande",
		genericErrorMessage:
			"Nous n'avons trouvé aucun résultat correspondant. Veuillez écrire à l'adresse ",
		genericErrorMessage2:
			" ou clavarder avec nous pour nous permettre de vous aider à trouver ce dont vous avez besoin.",
		validationErrorMessage:
			"Veuillez vérifier les champs surlignés et apporter les modifications nécessaires.",
		brandedEmail: getBrandInfo().email,
		easyQuotePageTitle: "Prêt à commander votre devis?",
		checkout: "Passer à la caisse en tant qu'invité",
		login: "Se connecter",
		or: "ou",
		back: "Retour",
		continue: "Continuer",
	},
	es: {
		dontHaveLoginText: "¿No tiene credenciales de inicio de sesión?",
		guestCheckoutDescription:
			"Puede solicitar acceso o simplemente hacer su pedido al convertir su cotización como invitado",
		customerNumberLabel: "Número de cliente",
		soldToZipCodeLabel: "Enviar al código postal",
		checkoutAsGuestButton: "Pagar como invitado",
		validateAccessText: "Valide su acceso",
		viewQuoteDetailsText: "Vea los detalles de la cotización",
		enterPurchaseOrderText: "Ingrese su número de orden de compra",
		enterInformationText: "Ingrese su información",
		selectPaymentMethodText: "Seleccione un método de pago",
		submitOrderText: "Enviar orden",
		genericErrorMessage: "No pudimos encontrar resultados coincidentes. Comuníquese con ",
		genericErrorMessage2: " o hable con nosotros para ayudarle a encontrar lo que necesita.",
		validationErrorMessage: "Revise los campos resaltados y haga los cambios necesarios.",
		brandedEmail: getBrandInfo().email,
		easyQuotePageTitle: "¿Listo para pedir lo que cotizó?",
		checkout: "Pagar como invitado",
		login: "Iniciar sesión",
		or: "o",
		back: "Atrás",
		continue: "Continuar",
	},
};
